import React, { useState, useEffect } from 'react';
import { Box, Chip, Tooltip, IconButton, Alert, AlertTitle, Collapse } from '@mui/material';
import { CheckCircle, Warning, Error as ErrorIcon, Refresh } from '@mui/icons-material';
import ServiceWorkerHealthCheck, { HealthCheckResult } from '../utils/notifications/ServiceWorkerHealthCheck';
import { useNotifications } from '../hooks/useNotifications';
import { useTranslation } from 'react-i18next';

/**
 * Indicateur visuel de l'état du Service Worker
 * Affiche un badge coloré selon la santé du SW
 */
export const ServiceWorkerStatusIndicator: React.FC = () => {
  const { t } = useTranslation();
  const [healthStatus, setHealthStatus] = useState<HealthCheckResult | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { subscribe } = useNotifications();

  useEffect(() => {
    // Démarrer la surveillance avec callback
    ServiceWorkerHealthCheck.startMonitoring((status) => {
      setHealthStatus(status);
    });

    // Première vérification
    ServiceWorkerHealthCheck.performHealthCheck().then(setHealthStatus);

    return () => {
      ServiceWorkerHealthCheck.stopMonitoring();
    };
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Forcer la vérification
      const status = await ServiceWorkerHealthCheck.performHealthCheck();
      setHealthStatus(status);

      // Si pas d'abonnement, essayer de se réabonner
      if (!status.hasSubscription && status.isActive) {
        const userId = localStorage.getItem('userId');
        if (userId) {
          console.log('[SW-STATUS] Attempting to resubscribe...');
          await subscribe(userId);
          // Re-vérifier après réabonnement
          const newStatus = await ServiceWorkerHealthCheck.performHealthCheck();
          setHealthStatus(newStatus);
        }
      }
    } catch (error) {
      console.error('[SW-STATUS] Error during refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!healthStatus) {
    return null; // Chargement initial
  }

  const getStatusColor = (): 'success' | 'warning' | 'error' => {
    if (healthStatus.isHealthy) return 'success';
    if (healthStatus.isRegistered && healthStatus.isActive) return 'warning';
    return 'error';
  };

  const getStatusIcon = () => {
    if (healthStatus.isHealthy) return <CheckCircle fontSize="small" />;
    if (healthStatus.isRegistered && healthStatus.isActive) return <Warning fontSize="small" />;
    return <ErrorIcon fontSize="small" />;
  };

  const getStatusLabel = (): string => {
    if (healthStatus.isHealthy) return t('serviceWorker.status.active');
    if (healthStatus.isRegistered && healthStatus.isActive) return t('serviceWorker.status.partial');
    return t('serviceWorker.status.disabled');
  };

  const getTooltipText = (): string => {
    if (healthStatus.isHealthy) {
      return t('serviceWorker.tooltip.healthy');
    }
    if (healthStatus.issues.length > 0) {
      return `${t('serviceWorker.tooltip.issuesDetected')}:\n${healthStatus.issues.join('\n')}`;
    }
    return t('serviceWorker.tooltip.unknown');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        position: 'relative',
        '&:hover .refresh-button': {
          opacity: 1,
          visibility: 'visible'
        }
      }}
    >
      <Tooltip title={getTooltipText()} arrow>
        <Chip
          icon={getStatusIcon()}
          label={getStatusLabel()}
          color={getStatusColor()}
          size="small"
          onClick={() => setShowDetails(!showDetails)}
          sx={{ cursor: 'pointer' }}
        />
      </Tooltip>

      <Tooltip title={t('serviceWorker.actions.checkAndRepair')}>
        <Box
          className="refresh-button"
          onClick={handleRefresh}
          sx={{
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.2s, visibility 0.2s',
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'action.hover',
            cursor: 'pointer',
            animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
            '&:hover': {
              backgroundColor: 'action.selected'
            },
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }}
        >
          <Refresh fontSize="small" />
        </Box>
      </Tooltip>

      {/* Détails des problèmes */}
      <Collapse in={showDetails && healthStatus.issues.length > 0} sx={{ position: 'absolute', top: 60, right: 16, zIndex: 9999 }}>
        <Alert
          severity={getStatusColor()}
          onClose={() => setShowDetails(false)}
          sx={{ minWidth: 300, maxWidth: 400 }}
        >
          <AlertTitle>{t('serviceWorker.details.title')}</AlertTitle>
          <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
            <li>{t('serviceWorker.details.registered')}: {healthStatus.isRegistered ? '✅' : '❌'}</li>
            <li>{t('serviceWorker.details.active')}: {healthStatus.isActive ? '✅' : '❌'}</li>
            <li>{t('serviceWorker.details.pushSubscription')}: {healthStatus.hasSubscription ? '✅' : '❌'}</li>
          </Box>
          {healthStatus.issues.length > 0 && (
            <>
              <strong>{t('serviceWorker.details.issues')}:</strong>
              <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                {healthStatus.issues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </Box>
            </>
          )}
          <Box sx={{ mt: 1, fontSize: '0.85em', color: 'text.secondary' }}>
            {t('serviceWorker.details.lastCheck')}: {healthStatus.lastCheck.toLocaleTimeString()}
          </Box>
        </Alert>
      </Collapse>
    </Box>
  );
};
